import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ImagePlus, Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, type AdminCategory, type AdminProduct, type ProductVariantInput } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";
import { ROUTES } from "@/constants/routes";

type FormState = {
    name: string;
    brand: string;
    slug: string;
    category_slug: string;
    price: number;
    mrp: number;
    weight: string;
    unit: string;
    stock_count: number;
    low_stock_threshold: number;
    tag: string;
    glyph: string;
    tone: string[];
    eta_minutes: number;
    description: string;
    ingredients: string;
    images: string[];
    variants: ProductVariantInput[];
    is_active: boolean;
    show_picture: boolean;
};

function emptyForm(): FormState {
    return {
        name: "",
        brand: "",
        slug: "",
        category_slug: "",
        price: 0,
        mrp: 0,
        weight: "",
        unit: "",
        stock_count: 0,
        low_stock_threshold: 5,
        tag: "",
        glyph: "T",
        tone: ["#dbeafe", "#93c5fd"],
        eta_minutes: 12,
        description: "",
        ingredients: "",
        images: [],
        variants: [],
        is_active: true,
        show_picture: false,
    };
}

export default function AdminProductForm() {
    const { id } = useParams<{ id?: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const qc = useQueryClient();

    const [form, setForm] = useState<FormState>(emptyForm);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data: categories = [] } = useQuery<AdminCategory[]>({
        queryKey: ["admin", "categories"],
        queryFn: adminApi.listCategories,
    });

    const { data: existing, isLoading } = useQuery<AdminProduct | undefined>({
        queryKey: ["admin", "product", id],
        queryFn: () => (id ? adminApi.getProduct(id) : Promise.resolve(undefined)),
        enabled: isEdit,
    });

    useEffect(() => {
        if (existing) {
            setForm({
                name: existing.name,
                brand: existing.brand,
                slug: existing.slug,
                category_slug: existing.category_slug,
                price: existing.price,
                mrp: existing.mrp,
                weight: existing.weight,
                unit: existing.unit,
                stock_count: existing.stock_count,
                low_stock_threshold: existing.low_stock_threshold,
                tag: existing.tag || "",
                glyph: existing.glyph,
                tone: existing.tone.length ? existing.tone : ["#dbeafe", "#93c5fd"],
                eta_minutes: existing.eta_minutes,
                description: existing.description,
                ingredients: existing.ingredients.join(", "),
                images: existing.images,
                variants: existing.variants,
                is_active: existing.is_active,
                show_picture: Boolean(existing.show_picture),
            });
        }
    }, [existing]);

    useEffect(() => {
        // Auto-pick first category on new form once list loads
        if (!isEdit && !form.category_slug && categories.length) {
            setForm((f) => ({ ...f, category_slug: categories[0].slug }));
        }
    }, [categories, isEdit, form.category_slug]);

    const onFile = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        setUploading(true);
        try {
            const uploaded: string[] = [];
            for (const file of Array.from(fileList).slice(0, 8 - form.images.length)) {
                const res = await adminApi.uploadImage(file, "products");
                uploaded.push(res.url);
            }
            setForm((f) => ({ ...f, images: [...f.images, ...uploaded].slice(0, 8) }));
            toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setUploading(false);
        }
    };

    const onDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        await onFile(e.dataTransfer.files);
    };

    const removeImage = (idx: number) =>
        setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

    const addVariant = () =>
        setForm((f) => ({
            ...f,
            variants: [...f.variants, { label: "", weight: "", unit: "", price: 0, mrp: 0 }],
        }));

    const updateVariant = (idx: number, patch: Partial<ProductVariantInput>) =>
        setForm((f) => ({
            ...f,
            variants: f.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)),
        }));

    const removeVariant = (idx: number) =>
        setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!form.category_slug) {
            setError("Please pick a category");
            return;
        }
        if (form.mrp < form.price) {
            setError("MRP must be ≥ selling price");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                slug: form.slug.trim() || undefined,
                brand: form.brand.trim(),
                category_slug: form.category_slug,
                price: Number(form.price),
                mrp: Number(form.mrp),
                weight: form.weight,
                unit: form.unit,
                stock_count: Number(form.stock_count),
                low_stock_threshold: Number(form.low_stock_threshold),
                tag: form.tag || null,
                glyph: form.glyph,
                tone: form.tone,
                eta_minutes: Number(form.eta_minutes),
                description: form.description,
                ingredients: form.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
                images: form.images,
                variants: form.variants.map((v) => ({
                    ...v,
                    price: Number(v.price),
                    mrp: Number(v.mrp),
                })),
                is_active: form.is_active,
                show_picture: form.show_picture,
            };
            if (isEdit && id) {
                await adminApi.updateProduct(id, payload);
                toast.success("Product updated");
            } else {
                await adminApi.createProduct(payload);
                toast.success("Product created");
            }
            qc.invalidateQueries({ queryKey: ["admin", "products"] });
            navigate(ROUTES.adminProducts);
        } catch (err) {
            setError(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const discountPct =
        form.mrp > 0 && form.mrp > form.price
            ? Math.round(((form.mrp - form.price) / form.mrp) * 100)
            : 0;

    if (isEdit && isLoading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} data-testid="admin-product-form" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <button
                        type="button"
                        onClick={() => navigate(ROUTES.adminProducts)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> All products
                    </button>
                    <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">
                        {isEdit ? "Edit product" : "Add new product"}
                    </h1>
                </div>
                <SubmitButton
                    type="submit"
                    fullWidth={false}
                    loading={saving}
                    data-testid="admin-product-save"
                >
                    {isEdit ? "Save changes" : "Create product"}
                </SubmitButton>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                {/* Left column */}
                <div className="lg:col-span-2 space-y-6">
                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft space-y-4">
                        <h3 className="text-lg font-bold">Basics</h3>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Name" required>
                                <Input
                                    data-testid="admin-product-name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    minLength={2}
                                />
                            </Field>
                            <Field label="Brand">
                                <Input
                                    data-testid="admin-product-brand"
                                    value={form.brand}
                                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                                />
                            </Field>
                            <Field label="Category" required>
                                <select
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                    data-testid="admin-product-category"
                                    value={form.category_slug}
                                    onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
                                    required
                                >
                                    <option value="">Pick a category</option>
                                    {categories.map((c) => (
                                        <option key={c.slug} value={c.slug}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Tag" hint="e.g. bestseller / new / deal">
                                <Input
                                    data-testid="admin-product-tag"
                                    value={form.tag}
                                    onChange={(e) => setForm({ ...form, tag: e.target.value })}
                                />
                            </Field>
                        </div>
                        <Field label="Description">
                            <textarea
                                className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="admin-product-description"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Short customer-facing description"
                            />
                        </Field>
                        <Field label="Ingredients" hint="Comma-separated">
                            <Input
                                data-testid="admin-product-ingredients"
                                value={form.ingredients}
                                onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                                placeholder="Wheat flour, salt, oil"
                            />
                        </Field>
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold">Images</h3>
                            <span className="text-xs text-muted-foreground">
                                {form.images.length}/8 · JPG, PNG, WEBP, SVG · ≤ 5 MB each
                            </span>
                        </div>
                        <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={onDrop}
                            className="grid grid-cols-2 gap-3 sm:grid-cols-4"
                        >
                            {form.images.map((url, idx) => (
                                <div key={url} className="relative aspect-square overflow-hidden rounded-xl border border-border/60">
                                    <img src={url} alt="" className="h-full w-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(idx)}
                                        data-testid={`admin-product-image-remove-${idx}`}
                                        className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            {form.images.length < 8 ? (
                                <label
                                    data-testid="admin-product-image-dropzone"
                                    className="grid aspect-square cursor-pointer place-items-center rounded-xl border-2 border-dashed border-border/80 bg-muted/30 text-center text-xs text-muted-foreground transition hover:border-brand hover:bg-brand/5"
                                >
                                    {uploading ? (
                                        <Loader2 className="h-5 w-5 animate-spin text-brand" />
                                    ) : (
                                        <div className="space-y-1 p-3">
                                            <ImagePlus className="mx-auto h-5 w-5 text-brand" />
                                            <p>Drop or click to upload</p>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        data-testid="admin-product-image-input"
                                        onChange={(e) => onFile(e.target.files)}
                                    />
                                </label>
                            ) : null}
                        </div>
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold">Variants</h3>
                            <button
                                type="button"
                                onClick={addVariant}
                                data-testid="admin-product-variant-add"
                                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                            >
                                <Plus className="h-3.5 w-3.5" /> Add variant
                            </button>
                        </div>
                        {form.variants.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No variants yet — add size / weight / unit alternatives.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {form.variants.map((v, i) => (
                                    <div key={i} className="grid gap-2 rounded-xl border border-border/50 p-3 sm:grid-cols-6">
                                        <Input
                                            placeholder="Label (e.g. Small)"
                                            value={v.label}
                                            onChange={(e) => updateVariant(i, { label: e.target.value })}
                                            className="sm:col-span-2"
                                            data-testid={`variant-label-${i}`}
                                        />
                                        <Input
                                            placeholder="Weight"
                                            value={v.weight}
                                            onChange={(e) => updateVariant(i, { weight: e.target.value })}
                                        />
                                        <Input
                                            placeholder="Unit"
                                            value={v.unit}
                                            onChange={(e) => updateVariant(i, { unit: e.target.value })}
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Price"
                                            value={v.price}
                                            onChange={(e) => updateVariant(i, { price: Number(e.target.value) })}
                                        />
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="number"
                                                placeholder="MRP"
                                                value={v.mrp}
                                                onChange={(e) => updateVariant(i, { mrp: Number(e.target.value) })}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeVariant(i)}
                                                className="grid h-8 w-8 place-items-center rounded-lg text-error hover:bg-error/10"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft space-y-4">
                        <h3 className="text-lg font-bold">Pricing</h3>
                        <div className="grid gap-3">
                            <Field label="Selling price (₹)" required>
                                <Input
                                    type="number"
                                    data-testid="admin-product-price"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                                    required
                                    min={0}
                                />
                            </Field>
                            <Field label="MRP (₹)" required>
                                <Input
                                    type="number"
                                    data-testid="admin-product-mrp"
                                    value={form.mrp}
                                    onChange={(e) => setForm({ ...form, mrp: Number(e.target.value) })}
                                    required
                                    min={0}
                                />
                            </Field>
                            {discountPct > 0 ? (
                                <p className="text-xs font-semibold text-emerald-600">
                                    {discountPct}% off — save ₹{form.mrp - form.price}
                                </p>
                            ) : null}
                        </div>
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft space-y-4">
                        <h3 className="text-lg font-bold">Inventory</h3>
                        <Field label="Stock quantity" required>
                            <Input
                                type="number"
                                data-testid="admin-product-stock"
                                value={form.stock_count}
                                onChange={(e) => setForm({ ...form, stock_count: Number(e.target.value) })}
                                min={0}
                                required
                            />
                        </Field>
                        <Field label="Low stock threshold">
                            <Input
                                type="number"
                                value={form.low_stock_threshold}
                                onChange={(e) => setForm({ ...form, low_stock_threshold: Number(e.target.value) })}
                                min={0}
                            />
                        </Field>
                        <Field label="Weight" hint="e.g. 500 g / 1 kg">
                            <Input
                                data-testid="admin-product-weight"
                                value={form.weight}
                                onChange={(e) => setForm({ ...form, weight: e.target.value })}
                            />
                        </Field>
                        <Field label="Unit" hint="pack of 6, 1 piece…">
                            <Input
                                value={form.unit}
                                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                            />
                        </Field>
                    </section>

                    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft space-y-4">
                        <h3 className="text-lg font-bold">Display</h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Glyph" hint="First letter shown when no image">
                                <Input
                                    value={form.glyph}
                                    onChange={(e) => setForm({ ...form, glyph: e.target.value.slice(0, 1).toUpperCase() })}
                                    maxLength={1}
                                    className="uppercase"
                                />
                            </Field>
                            <Field label="ETA (minutes)">
                                <Input
                                    type="number"
                                    value={form.eta_minutes}
                                    onChange={(e) => setForm({ ...form, eta_minutes: Number(e.target.value) })}
                                    min={1}
                                />
                            </Field>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                            <div>
                                <p className="text-sm font-semibold">📷 Show Product Picture</p>
                                <p className="text-xs text-muted-foreground">
                                    Use the uploaded image as the primary display everywhere. Falls back to the glyph placeholder if no image is uploaded.
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                data-testid="admin-product-show-picture"
                                checked={form.show_picture}
                                onChange={(e) => setForm({ ...form, show_picture: e.target.checked })}
                                className="h-5 w-5 rounded"
                            />
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                            <div>
                                <p className="text-sm font-semibold">Product is live</p>
                                <p className="text-xs text-muted-foreground">
                                    Toggle off to save as a draft (hidden from shoppers)
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                data-testid="admin-product-active"
                                checked={form.is_active}
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                className="h-5 w-5 rounded"
                            />
                        </div>
                    </section>

                    {error ? (
                        <p role="alert" data-testid="admin-product-error" className="text-sm font-medium text-error">
                            {error}
                        </p>
                    ) : null}
                </div>
            </div>
        </form>
    );
}

function Field({
    label,
    hint,
    required,
    children,
}: {
    label: string;
    hint?: string;
    required?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <Label className="flex items-center gap-1">
                {label}
                {required ? <span className="text-error">*</span> : null}
            </Label>
            {children}
            {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
        </div>
    );
}
